import { useField } from 'formik';
import { Combobox } from './Combobox';

type Props = {
  name: string;
  label?: string;
  items: string[];
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  /** When true, shows a SearchInput component for filtering, similar to SiteSelect */
  searchable?: boolean;
  /** When true the "Create" option is always displayed, even when nothing is entered in the input */
  createAlwaysShown?: boolean;
  /** Custom text for the create option when createAlwaysShown is true and no query */
  createText?: string;
  /** When false, never show the create option (even if query not in list) */
  showCreateOption?: boolean;
  /** Additional CSS classes to apply to the wrapper */
  className?: string;
  /** Custom onChange handler - if provided, will be called instead of default behavior */
  onChange?: (value: string) => void;
  /** Control sorting behavior in the underlying Combobox (defaults to true there) */
  sortItems?: boolean;
  /** Control data-usetiful attribute in the underlying Combobox (defaults to empty string there) */
  dataUsetiful?: string;
};

export const ComboboxField = ({
  name,
  searchable = false,
  className,
  onChange,
  sortItems,
  ...rest
}: Props) => {
  const [field, meta, helpers] = useField(name);

  function handleChange(value: string) {
    if (onChange) {
      // Use custom onChange if provided
      onChange(value);
    } else {
      // Default behavior
      helpers.setValue(value);
      helpers.setTouched(true, false);
    }
  }

  // Only show error if field has been touched AND user has stopped interacting
  // This prevents shake animation during normal typing/selection
  const shouldShowError = meta.touched && meta.error && !field.value;

  return (
    <Combobox
      value={field.value}
      onChange={handleChange}
      searchable={searchable}
      sortItems={sortItems}
      error={shouldShowError ? meta.error : undefined}
      showCreateOption={rest.showCreateOption}
      {...rest}
    />
  );
};
